import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminLayout } from './AdminLayout';
import { api } from '../../lib/api';

interface PlatformStats {
  totalUsers: number;
  activeUsers: number;
  totalLeagues: number;
  activeLeagues: number;
  competitions: number;
  predictions: number;
  leagueMatchdays: number;
  openDisputes: number;
  failedAiJobs: number;
  recentAudit: number;
}

/**
 * Panoramica piattaforma (lineeguida §42): metriche come testo/dl,
 * sempre leggibili — niente grafici senza alternativa.
 */
export function AdminDashboardPage() {
  usePageTitle('Amministrazione');
  const stats = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => api<{ stats: PlatformStats }>('/admin/stats').then((r) => r.stats),
  });

  const tiles: Array<{ label: string; value: number | undefined; alert?: boolean }> = [
    { label: 'Utenti totali', value: stats.data?.totalUsers },
    { label: 'Utenti attivi (30 giorni)', value: stats.data?.activeUsers },
    { label: 'Leghe totali', value: stats.data?.totalLeagues },
    { label: 'Leghe attive', value: stats.data?.activeLeagues },
    { label: 'Campionati', value: stats.data?.competitions },
    { label: 'Schedine giocate', value: stats.data?.predictions },
    { label: 'Giornate di lega', value: stats.data?.leagueMatchdays },
    { label: 'Contestazioni aperte', value: stats.data?.openDisputes, alert: true },
    { label: 'Errori AI', value: stats.data?.failedAiJobs, alert: true },
    { label: 'Operazioni audit (30 giorni)', value: stats.data?.recentAudit },
  ];

  return (
    <AdminLayout title="Amministrazione piattaforma">
      {stats.isLoading && (
        <p role="status" className="mt-6">Caricamento metriche…</p>
      )}
      {stats.data && (
        <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className={`rounded-2xl border p-4 ${
                tile.alert && (tile.value ?? 0) > 0
                  ? 'border-warning bg-amber-50 dark:bg-ink-900'
                  : 'border-ink-200 dark:border-ink-800'
              }`}
            >
              <dt className="text-sm text-ink-600 dark:text-ink-300">{tile.label}</dt>
              <dd className="text-3xl font-bold">{tile.value ?? '—'}</dd>
            </div>
          ))}
        </dl>
      )}
    </AdminLayout>
  );
}
